import { z } from 'zod';
import { isBirthDate, parseBirthDate } from '../birthdate';

/** 'YYYY-MM-DD'. Date-only everywhere a fixture or a birthday is meant. */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
  .refine((s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
  }, 'not a real date');

/**
 * Optional free text that treats blank input as absent.
 *
 * Forms submit "" for every untouched field, and storing that instead of NULL
 * makes "this player has no nickname" indistinguishable from "their nickname is
 * blank". The transform must come before .optional(): a union would match the
 * plain-string branch first and never reach it.
 */
const optionalText = (max: number) =>
  // Accepts null as well as undefined — see the note in ./match.ts.
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v == null || v === '' ? undefined : v))
    .optional();

/**
 * A birth date at whatever precision is known: 'YYYY-MM-DD', 'YYYY-MM' or
 * 'YYYY'. Anything else a coach types is run through the same reader the
 * importer uses, so the form accepts '03/2008' and stores '2008-03'.
 */
export const birthDateSchema = z
  .string()
  .trim()
  .transform((v) => (isBirthDate(v) ? v : (parseBirthDate(v) ?? v)))
  .refine(isBirthDate, 'expected a year, a month and year, or a full date');

export const strongFootSchema = z.enum(['left', 'right', 'both']);
export const genderSchema = z.enum(['male', 'female', 'mixed']);
export const seasonStatusSchema = z.enum(['planning', 'active', 'archived']);

// ── Team ───────────────────────────────────────────────────────────────

export const teamInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  /** Free text, not an enum: U13, U19, Senior, Réserve… */
  ageCategory: optionalText(40),
  gender: genderSchema.optional(),
  /** A formation is not a game model; the game model arrives in M3. */
  baseFormation: optionalText(20),
  notes: optionalText(2000),
});

// ── Season and phases ──────────────────────────────────────────────────

/**
 * A season ending before it starts silently breaks every calendar and load
 * window built on top of it, so the range is checked at the edge rather than
 * discovered later as a blank chart.
 */
const endsAfterStarts = {
  message: 'ends_before_starts',
  path: ['endsOn'],
};

export const seasonInputSchema = z
  .object({
    teamId: z.string().min(1).max(64),
    label: z.string().trim().min(1).max(80),
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    status: seasonStatusSchema.default('planning'),
  })
  .refine((v) => v.startsOn <= v.endsOn, endsAfterStarts);

/**
 * Editing a season, which until now could only be created.
 *
 * The team is not in it: a season belongs to the squad that played it, and
 * moving one to another team would quietly reattach a year of work. Everything
 * else changes, because a season's dates are a guess until the fixture list
 * arrives and the first session often turns out to predate them.
 */
export const seasonEditSchema = z
  .object({
    label: z.string().trim().min(1).max(80),
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    status: seasonStatusSchema,
  })
  .refine((v) => v.startsOn <= v.endsOn, endsAfterStarts);

export const seasonPhaseInputSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    /** Optional hint only — pre-season, tournament block, rehab window… */
    kind: optionalText(40),
    startsOn: isoDateSchema,
    endsOn: isoDateSchema,
    objectiveSummary: optionalText(500),
    sort: z.number().int().min(0).max(9999).default(0),
  })
  .refine((v) => v.startsOn <= v.endsOn, endsAfterStarts);

// ── Player ─────────────────────────────────────────────────────────────

/**
 * Only what coaching needs. No address, no phone, no guardian contact: many of
 * these players are minors, and a field that is not collected cannot leak.
 */
export const playerInputSchema = z.object({
  firstName: z.string().trim().min(1).max(60),
  lastName: z.string().trim().min(1).max(60),
  knownAs: optionalText(60),
  dateOfBirth: birthDateSchema.optional(),
  nationality: optionalText(60),
  strongFoot: strongFootSchema.optional(),
  heightCm: z.number().min(100).max(230).optional(),
  weightKg: z.number().min(25).max(160).optional(),
  // Wider than a position key, because a squad list arrives saying "Défenseur
  // central" and the server resolves it. An unresolved value is stored as
  // written so the coach can see it and fix it, rather than losing the row.
  primaryPositionKey: optionalText(40),
  secondaryPositionKey: optionalText(40),
  notes: optionalText(2000),
  /** Squad membership created alongside the player, when a team is given. */
  teamId: z.string().min(1).max(64).optional(),
  shirtNumber: z.number().int().min(1).max(99).optional(),
  statusKey: optionalText(40),
  /** How important this player is to the team, independent of this week's status. */
  roleKey: optionalText(40),
});

export const playerPatchSchema = playerInputSchema.partial();

/**
 * One patch, many players.
 *
 * Shirt number is omitted on purpose: it is unique within a team, so applying
 * one to a selection is either a mistake or a pile of conflicts.
 */
export const playerBulkPatchSchema = z.object({
  playerIds: z.array(z.string().min(1).max(64)).min(1).max(200),
  // Strict, so a shirt number sent by mistake is refused rather than dropped.
  // Silently ignoring a field the coach asked for is the same class of defect as
  // silently ignoring a position the importer could not read.
  patch: playerPatchSchema.omit({ shirtNumber: true }).strict(),
});

export type PlayerBulkPatch = z.infer<typeof playerBulkPatchSchema>;

export const squadMembershipSchema = z.object({
  teamId: z.string().min(1).max(64),
  shirtNumber: z.number().int().min(1).max(99).optional(),
  statusKey: optionalText(40),
  roleKey: optionalText(40),
  joinedOn: isoDateSchema.optional(),
  isPrimaryTeam: z.boolean().default(true),
});

/**
 * Bulk import. Capped at 200 rows because a squad is a squad — anything larger
 * is a mistaken file, and letting it through would mean a partial import of
 * something the coach did not mean to send.
 */
/**
 * A club's own field, named by whoever mapped it.
 *
 * `key` is derived from the label on the server so two spellings of the same
 * heading land on one column; a caller may pass one to reuse a field it already
 * knows about.
 */
export const fieldDefInputSchema = z.object({
  key: z.string().trim().max(48).optional(),
  label: z.string().trim().min(1).max(60),
  kind: z.enum(['text', 'number', 'date', 'phone', 'email', 'url']).optional(),
  isSensitive: z.boolean().optional(),
});

/** Custom values for one player: { field key or label → value }. */
export const attributesSchema = z.record(
  z.string().trim().min(1).max(60),
  z.string().trim().max(500).nullable(),
);

export const playerBulkSchema = z.object({
  teamId: z.string().min(1).max(64).optional(),
  players: z
    .array(playerInputSchema.extend({ attributes: attributesSchema.optional() }))
    .min(1)
    .max(200),
  /**
   * Columns the coach mapped that this product has no field for. Declared
   * alongside the rows so a field is created once, with its label, rather than
   * inferred from whichever row happened to be first.
   */
  fields: z.array(fieldDefInputSchema).max(40).optional(),
});

/** Per-row outcome, so a coach sees exactly which lines did not land and why. */
export interface BulkRowResult {
  row: number;
  ok: boolean;
  id?: string;
  error?: string;
  field?: string;
}

export interface BulkResult {
  created: number;
  failed: number;
  results: BulkRowResult[];
}

export type PlayerBulkInput = z.infer<typeof playerBulkSchema>;
export type FieldDefInput = z.infer<typeof fieldDefInputSchema>;

export type TeamInput = z.infer<typeof teamInputSchema>;
export type SeasonInput = z.infer<typeof seasonInputSchema>;
export type SeasonEditInput = z.infer<typeof seasonEditSchema>;
export type SeasonPhaseInput = z.infer<typeof seasonPhaseInputSchema>;
export type PlayerInput = z.infer<typeof playerInputSchema>;
export type PlayerPatch = z.infer<typeof playerPatchSchema>;

// ── Read models returned to the SPA ─────────────────────────────────────

export interface TeamRecord {
  id: string;
  name: string;
  ageCategory: string | null;
  gender: string | null;
  baseFormation: string | null;
  notes: string | null;
  playerCount: number;
  archivedAt: number | null;
}

export interface PlayerRecord {
  id: string;
  firstName: string;
  lastName: string;
  knownAs: string | null;
  dateOfBirth: string | null;
  nationality: string | null;
  strongFoot: string | null;
  heightCm: number | null;
  weightKg: number | null;
  primaryPositionKey: string | null;
  secondaryPositionKey: string | null;
  notes: string | null;
  archivedAt: number | null;
  /** Present when the player is read in the context of one team. */
  shirtNumber?: number | null;
  statusKey?: string | null;
  roleKey?: string | null;
  teamId?: string | null;
  /** The club's own fields, keyed by player_field_def.key. */
  attributes?: Record<string, string>;
}

export interface ConfigRecord {
  id: string;
  key: string;
  label: string;
  i18nKey: string | null;
  sort: number;
  isActive: boolean;
}

export interface PositionRecord extends ConfigRecord {
  unit: 'gk' | 'defence' | 'midfield' | 'attack';
  /** Other positions a player here can also fill. Owned by the club. */
  covers?: string[];
}

export interface PlayerStatusRecord extends ConfigRecord {
  /** squad = standing this week · role = importance to the team · trial = selection */
  kind: 'squad' | 'role' | 'trial';
}


// ── Calendar ───────────────────────────────────────────────────────────

export const eventKindSchema = z.enum([
  'training', 'match', 'friendly', 'test', 'meeting', 'trial', 'recovery', 'other',
]);
export const eventStatusSchema = z.enum(['scheduled', 'postponed', 'cancelled', 'played']);
export const venueSchema = z.enum(['home', 'away', 'neutral']);

/** 'HH:MM', 24-hour. */
const timeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM');

export const calendarEventInputSchema = z
  .object({
    teamId: z.string().min(1).max(64),
    seasonId: z.string().min(1).max(64).optional(),
    kind: eventKindSchema,
    title: optionalText(120),
    onDate: isoDateSchema,
    startsAt: timeSchema.optional().or(z.literal('').transform(() => undefined)),
    endsAt: timeSchema.optional().or(z.literal('').transform(() => undefined)),
    location: optionalText(120),
    opponent: optionalText(120),
    venue: venueSchema.optional(),
    competition: optionalText(80),
    status: eventStatusSchema.default('scheduled'),
    changeReason: optionalText(500),
    notes: optionalText(2000),
  })
  .refine((v) => !v.startsAt || !v.endsAt || v.startsAt <= v.endsAt, {
    message: 'ends_before_starts',
    path: ['endsAt'],
  });

export type CalendarEventInput = z.infer<typeof calendarEventInputSchema>;

export interface CalendarEventRecord {
  id: string;
  teamId: string;
  seasonId: string | null;
  kind: string;
  title: string | null;
  onDate: string;
  startsAt: string | null;
  endsAt: string | null;
  location: string | null;
  opponent: string | null;
  venue: string | null;
  competition: string | null;
  status: string;
  changeReason: string | null;
  notes: string | null;
}


// ── Exercise library ───────────────────────────────────────────────────

export const dimensionSchema = z.enum([
  'technical', 'tactical', 'physical', 'mental', 'social',
]);
export const emphasisSchema = z.enum(['primary', 'secondary']);

export const exerciseObjectiveInputSchema = z.object({
  dimension: dimensionSchema,
  emphasis: emphasisSchema.default('secondary'),
  text: z.string().trim().min(1).max(300),
});

export const exerciseInputSchema = z.object({
  name: z.string().trim().min(1).max(160),
  categoryKey: optionalText(40),
  description: optionalText(4000),

  minPlayers: z.number().int().min(1).max(60).optional(),
  maxPlayers: z.number().int().min(1).max(60).optional(),
  areaLength: z.number().min(1).max(130).optional(),
  areaWidth: z.number().min(1).max(100).optional(),

  durationMin: z.number().min(1).max(180).optional(),
  sets: z.number().int().min(1).max(30).optional(),
  reps: z.number().int().min(1).max(100).optional(),
  workSeconds: z.number().int().min(1).max(3600).optional(),
  restSeconds: z.number().int().min(0).max(3600).optional(),
  intensity: z.number().min(0).max(10).optional(),

  organization: optionalText(4000),
  rules: optionalText(4000),
  coachingPoints: optionalText(4000),
  progression: optionalText(2000),
  regression: optionalText(2000),
  successCriteria: optionalText(2000),
  observationCriteria: optionalText(2000),
  equipment: optionalText(1000),

  /** Why this exercise exists. The why-engine reads this. */
  rationale: optionalText(2000),

  /** The board's serialised state, as the canvas produces it. */
  diagramJson: z.string().max(400_000).optional(),

  objectives: z.array(exerciseObjectiveInputSchema).max(15).default([]),
  energySystems: z
    .array(z.object({ systemKey: z.string().min(1).max(40), emphasis: emphasisSchema }))
    .max(3)
    .default([]),
  qualities: z
    .array(z.object({ qualityKey: z.string().min(1).max(40), emphasis: emphasisSchema }))
    .max(20)
    .default([]),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
})
  .refine(
    (v) => v.minPlayers === undefined || v.maxPlayers === undefined || v.minPlayers <= v.maxPlayers,
    { message: 'min_above_max', path: ['maxPlayers'] },
  )
  .refine(
    (v) => v.objectives.filter((o) => o.emphasis === 'primary').length <= 1,
    // The brief is explicit: one primary objective, any number of secondary ones.
    // Two primaries means the exercise has no single reason for existing.
    { message: 'one_primary_only', path: ['objectives'] },
  );

export type ExerciseInput = z.infer<typeof exerciseInputSchema>;
export type ExerciseObjectiveInput = z.infer<typeof exerciseObjectiveInputSchema>;

export interface ExerciseRecord {
  id: string;
  name: string;
  categoryKey: string | null;
  description: string | null;
  minPlayers: number | null;
  maxPlayers: number | null;
  areaLength: number | null;
  areaWidth: number | null;
  durationMin: number | null;
  sets: number | null;
  reps: number | null;
  workSeconds: number | null;
  restSeconds: number | null;
  intensity: number | null;
  organization: string | null;
  rules: string | null;
  coachingPoints: string | null;
  progression: string | null;
  regression: string | null;
  successCriteria: string | null;
  observationCriteria: string | null;
  equipment: string | null;
  rationale: string | null;
  diagramJson: string | null;
  archivedAt: number | null;
}

export interface ExerciseDetail extends ExerciseRecord {
  objectives: Array<{ id: string; dimension: string; emphasis: string; text: string }>;
  energySystems: Array<{ systemKey: string; emphasis: string }>;
  qualities: Array<{ qualityKey: string; emphasis: string }>;
  tags: string[];
}
